// Pure helpers for the "Agent" section on the booking-group Details tab. The
// agent link lives on each student's STAY (one agent per stay, V151), not on the
// group — so the group's agent is derived: resolve every student's in-group
// stay, then read the link each one carries (V152).

const d10 = (s) => (s ? String(s).slice(0, 10) : "");
const normCampus = (v) => String(v ?? "").trim().toLowerCase();

// Which of a student's stays is "the group stay": exact campus + window match
// first, else the single stay overlapping the window. Ambiguity → null, so we
// never link (or report) the wrong stay. Same rule as GroupConnectionDialog.
export function resolveGroupStay(stays, { campus, start, end }) {
  const list = Array.isArray(stays) ? stays : [];
  const c = normCampus(campus);
  const exact = list.find(
    (s) =>
      (!c || normCampus(s.campus) === c) &&
      d10(s.arrivalDate) === start &&
      d10(s.departureDate) === end
  );
  if (exact) return exact;
  if (!start || !end) return list.length === 1 ? list[0] : null;
  const overlapping = list.filter(
    (s) => d10(s.arrivalDate) <= end && start <= d10(s.departureDate)
  );
  return overlapping.length === 1 ? overlapping[0] : null;
}

// Group the resolved rows by agent so the section can say "Agent X · 14 of 15
// stays" — or list several when a group has been split between agents.
// rows: [{ studentId, name, stay|null }]
export function summariseAgents(rows) {
  const byAgent = new Map();
  let unlinked = 0;
  let noStay = 0;
  rows.forEach((r) => {
    if (!r.stay) {
      noStay += 1;
      return;
    }
    if (r.stay.agentId == null) {
      unlinked += 1;
      return;
    }
    const key = String(r.stay.agentId);
    if (!byAgent.has(key)) {
      byAgent.set(key, {
        agentId: r.stay.agentId,
        agentName: r.stay.agentName || `Agent #${r.stay.agentId}`,
        count: 0,
        // Snapshot terms seen on the stays — usually one pair; more means they differ.
        terms: new Set(),
      });
    }
    const a = byAgent.get(key);
    a.count += 1;
    if (r.stay.commission != null) {
      a.terms.add(`${r.stay.commission}${r.stay.commissionRate != null ? ` ${r.stay.commissionRate}%` : ""}`);
    }
  });
  const agents = Array.from(byAgent.values())
    .map((a) => ({ ...a, terms: Array.from(a.terms) }))
    .sort((a, b) => b.count - a.count || a.agentName.localeCompare(b.agentName));
  return { agents, unlinked, noStay, linked: rows.length - unlinked - noStay };
}

// What one "apply agent to the group" save has to do per stay. Kept separate so
// it can be shown before it runs and tested without a network.
//   target: { agentId, commission, commissionRate }
// → { link: [row], move: [row], update: [row], keep: [row] }
export function planAgentChanges(rows, target) {
  const plan = { link: [], move: [], update: [], keep: [] };
  rows.forEach((r) => {
    if (!r.stay) return;
    const s = r.stay;
    if (s.agentId == null) {
      plan.link.push(r);
    } else if (String(s.agentId) !== String(target.agentId)) {
      plan.move.push(r);
    } else if (
      (s.commission || null) !== (target.commission || null) ||
      Number(s.commissionRate ?? 0) !== Number(target.commissionRate ?? 0)
    ) {
      plan.update.push(r);
    } else {
      plan.keep.push(r);
    }
  });
  return plan;
}
